import { killAvatarOnTouch } from './primitives/killAvatarOnTouch'
import { createRaceTimer } from './primitives/raceTimer'
import { createWarpPadHandler } from './primitives/warpPad'
import { BehaviorHandler, MissionBehaviors, HudStyle } from './types'

/**
 * RJF08（M8）のミッション環境ビヘイビア。
 *
 * テンプレート rbxl（src/missions/templates/RJF08.rbxl）のギミックを移植する:
 * - StartGoal/Start + StarterGui/LocalScript: RJF07 と同型のタイマー
 *   （接触で開始、-1 始まりで毎秒 +1、最初の表示は 0、進行中は再起動しない）。
 * - StartGoal/Collision（ゴール）GoalScript: 接触でタイマー停止。接触のたびに
 *   JumpPower=50 / WalkSpeed=16 へ「リセット」する（RJF07 と逆で、コース中の
 *   強化をゴールで標準へ戻す）。
 * - TrapFolder/Trap（炎の箱 ×4）TrapScript: 接触で体力 0。
 *   （⑤トラップは学習者の操作対象でスクリプトなし。対象外。）
 * - Warp（ワープ元 ×4）WarpScript: 同じ ID 属性のワープ先 + (0,5,0) へ
 *   テレポート。シーンの名前連番（元#N ↔ 先#N）が ID 対応と一致するため、
 *   接尾辞ペアで解決する。
 *
 * プレビューでの近似・意図的な差異:
 * - タイマー表示は HUD テキスト（setHudText）。BGM・ゴール音はアセット取得
 *   不可のため、ゴール時のみ合成ジングルで近似する（rjf07 と同様）。
 *
 * タイマー・ワープの実装は rjf07 と同型のため primitives/ を構成する
 * （世代トークンの設計判断は primitives/raceTimer.ts を参照）。
 */

/** ゴール接触時のジャンプ力・歩行速度（Lua: JumpPower=50, WalkSpeed=16）。 */
const GOAL_JUMP_POWER = 50
const GOAL_WALK_SPEED = 16

/** スタートゲートのオブジェクト名。 */
const START_NAME = 'Start'

/** ゴール（StartGoal/Collision）のオブジェクト名。 */
const GOAL_NAME = 'Collision'

/** トラップ（炎の箱）のオブジェクト名。⑤トラップ（学習者の対象）は含めない。 */
const TRAP_NAMES: ReadonlyArray<string> = ['Trap', 'Trap#2', 'Trap#3', 'Trap#4']

/**
 * ワープ元 → ワープ先のオブジェクト名の対応（rbxl の ID 属性 1〜4 と
 * シーンの名前連番が一致することを実測済み）。
 */
const WARP_PAIRS: ReadonlyArray<{ from: string; to: string }> = [
  { from: 'ワープ元', to: 'ワープ先' },
  { from: 'ワープ元#2', to: 'ワープ先#2' },
  { from: 'ワープ元#3', to: 'ワープ先#3' },
  { from: 'ワープ元#4', to: 'ワープ先#4' },
]

/** タイマー GUI の実機デザイン（ScreenGui/TextLabel の実測値）。 */
const TIMER_HUD_STYLE: HudStyle = {
  background: '#FFFF7F',
  color: '#000000',
  border: '#FF0000',
  borderWidth: 3,
  left: 5,
}

export const createRjf08Behaviors = (): MissionBehaviors => {
  const timer = createRaceTimer({
    hudStyle: TIMER_HUD_STYLE,
    // Lua: GoalScript は接触のたびにジャンプ力・速度を標準へ戻す。
    applyGoalEffect: (world) => {
      world.setAvatarJumpPower(GOAL_JUMP_POWER)
      world.setAvatarWalkSpeed(GOAL_WALK_SPEED)
    },
  })

  const onTouchCharacter = new Map<string, BehaviorHandler>()
  onTouchCharacter.set(START_NAME, timer.onStartTouched)
  onTouchCharacter.set(GOAL_NAME, timer.onGoalTouched)
  for (const name of TRAP_NAMES) {
    // Lua: TrapScript は接触で Humanoid.Health = 0（死亡演出 → スポーン復帰）。
    onTouchCharacter.set(name, killAvatarOnTouch)
  }
  for (const pair of WARP_PAIRS) {
    onTouchCharacter.set(pair.from, createWarpPadHandler(pair.to))
  }
  return { onPlay: [timer.showInitialTimer], onTouchCharacter }
}
